import { Link } from 'react-router-dom';
import { Shield, Phone, CheckCircle, Award, Users, Clock, MapPin } from 'lucide-react';
import { SEO } from '../components/SEO';

// Images
const IMAGES = {
  k9Handler: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/jp5zo9ku_IMG_1273.jpeg",
  executive: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/ouzxb1wd_IMG_1274.jpeg",
  k9Dog: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/0h56cfcn_IMG_1272.jpeg",
  logo: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/223cawun_1508.png",
};

const stats = [
  { value: '24/7', label: 'Dispatch Available' },
  { value: 'PPO', label: '#15982 Licensed' },
  { value: '100%', label: 'Bonded & Insured' },
];

const values = [
  {
    icon: <Shield className="h-6 w-6" />,
    title: 'Integrity',
    description: 'We uphold the highest standards of honesty and ethical conduct in all our operations.'
  },
  {
    icon: <Award className="h-6 w-6" />,
    title: 'Excellence',
    description: 'We strive for excellence in every aspect of our security services and client relationships.'
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: 'Teamwork',
    description: 'Our strength lies in our dedicated team of trained security professionals working together.'
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Reliability',
    description: 'Count on us 24/7 for consistent, dependable security coverage when you need it most.'
  },
];

export const About = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="about-page">
      <SEO 
        title="About Us" 
        description="Surfside Patrol is a licensed private security company (PPO #15982) serving Southern California. 24/7 dispatch, bonded & insured. Learn about our mission and values."
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#121212]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
                About Surfside Patrol
              </p>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-6 font-['Barlow_Condensed']">
                Protecting What<br />
                <span className="text-[#F5C518]">Matters Most</span>
              </h1>
              <p className="text-lg text-zinc-400 leading-relaxed mb-8">
                Surfside Patrol is a licensed private security company serving Southern California. 
                We provide comprehensive security solutions including executive protection, K9 units, 
                drone surveillance, and 24/7 monitoring services.
              </p>
              <div className="flex items-center space-x-4">
                <img
                  src={IMAGES.logo}
                  alt="Surfside Patrol Logo"
                  className="h-20 w-20 object-contain"
                />
                <div>
                  <p className="text-white font-bold text-lg">Licensed & Insured</p>
                  <p className="text-[#F5C518] font-bold">PPO #15982</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={IMAGES.k9Handler}
                alt="K9 Security Handler"
                className="w-full aspect-[3/4] object-cover border border-zinc-800"
              />
              <div className="space-y-4">
                <img
                  src={IMAGES.executive}
                  alt="Executive Protection"
                  className="w-full aspect-video object-cover border border-zinc-800"
                />
                <img
                  src={IMAGES.k9Dog}
                  alt="Security K9"
                  className="w-full aspect-video object-cover border border-zinc-800"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0A0A0A] border-y border-zinc-800" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#F5C518] font-['Barlow_Condensed']">
                  {stat.value}
                </p>
                <p className="text-zinc-500 text-sm uppercase tracking-wider mt-2">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
              Our Mission
            </p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Professional Security You Can Trust
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed">
              Our mission is to provide exceptional security services that protect our clients' 
              people, property, and assets. We combine advanced technology with highly trained 
              personnel to deliver comprehensive security solutions tailored to each client's 
              unique needs. With 24-hour dispatch and monitoring capabilities, we ensure 
              round-the-clock protection for the communities we serve.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#121212]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
              Our Values
            </p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
              What We Stand For
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="text-center p-6 border border-zinc-800 bg-[#0A0A0A]/50"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wide text-white mb-2 font-['Barlow_Condensed']">
                  {value.title}
                </h3>
                <p className="text-zinc-500 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="about-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
                What We Offer
              </p>
              <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                Comprehensive Security Services
              </h2>
              <ul className="space-y-4 mb-8">
                {[
                  'Executive Protection for VIPs and corporate clients',
                  'K9 Property Guarding with trained handlers',
                  'Advanced Drone Surveillance with Matrice 30T',
                  'Armed and Unarmed Security Officers',
                  'Mobile Patrol Services',
                  'CCTV Installation and 24/7 Monitoring'
                ].map((service) => (
                  <li key={service} className="flex items-center text-zinc-400">
                    <CheckCircle className="h-5 w-5 text-[#F5C518] mr-3 flex-shrink-0" />
                    {service}
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300"
                data-testid="about-view-services"
              >
                <span>View All Services</span>
              </Link>
            </div>
            <div className="bg-[#121212] border border-zinc-800 p-8">
              <h3 className="text-xl font-bold uppercase tracking-wide text-white mb-6 font-['Barlow_Condensed']">
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-[#F5C518] mr-4 mt-1" />
                  <div>
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">Phone</p>
                    <a href="tel:714-588-2460" className="text-white font-bold text-lg hover:text-[#F5C518]">
                      714-588-2460
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-[#F5C518] mr-4 mt-1" />
                  <div>
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">Address</p>
                    <p className="text-white">
                      2102 Business Center Dr<br />
                      Irvine, CA 92612
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-[#F5C518] mr-4 mt-1" />
                  <div>
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">License</p>
                    <p className="text-[#F5C518] font-bold">PPO #15982</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="h-5 w-5 text-[#F5C518] mr-4 mt-1" />
                  <div>
                    <p className="text-zinc-500 text-sm uppercase tracking-wider">Availability</p>
                    <p className="text-white font-bold">24/7 Dispatch</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#121212] border-t border-zinc-800" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
            Get Started Today
          </p>
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-4">
            Ready to Discuss Your Security Needs?
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8">
            Contact our team for a free security assessment. We'll work with you to 
            develop a customized security plan that fits your specific requirements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300"
              data-testid="about-get-quote"
            >
              <span>Get Free Quote</span>
            </Link>
            <a
              href="tel:714-588-2460"
              className="inline-flex items-center justify-center border border-zinc-700 text-white hover:border-[#F5C518] hover:text-[#F5C518] font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
              data-testid="about-call-now"
            >
              <Phone className="h-5 w-5 mr-2" />
              <span>714-588-2460</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
